import React, { useEffect, useState } from 'react';
import { useGet } from 'restful-react';

import './ProjectSummaryCard.scss';

import { useTranslation } from 'react-i18next';
import { Button } from '../../../FormComponents/Buttons/Button';
import LoadingSpinner from '../../../LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../../ErrorMessage/ErrorMessage';

import BackArrow from '../../../../images/back-arrow-blue.png';
import ProjectSummaryCardBlockProjectUser from '../ProjectSummaryCardBlocks/ProjectSummaryCardBlockProjectUser';
import ProjectSummaryCardBlockGeneral from '../ProjectSummaryCardBlocks/ProjectSummaryCardBlockGeneral';
import ProjectSummaryCardBlockLinks from '../ProjectSummaryCardBlocks/ProjectSummaryCardBlockLinks';
import { OkResponse } from '../../../../common/interfaces';
import { Project } from '../../../../types/Project';
import Chat from '../../../Chat/Chat';
import { DuplicateProjectDetailsModal } from '../../../Modals/DuplicateProjectDetailsModal/DuplicateProjectDetailsModal';
import { CaptionModeEnum } from '../../../../types/enums/CaptionModeEnum';
import { Destination, Destinations } from '../../../../types/Destination';
import { UserTypeEnum } from '../../../../types/enums/UserTypesEnum';

type ProjectSummaryCardProps = {
  projectId: string;
  onBackButton: () => void;
};

const ProjectSummaryCard = ({ projectId, onBackButton }: ProjectSummaryCardProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [duplicateCaptioning, setDuplicateCaptioning] = useState(false);
  const [duplicateConnectors, setDuplicateConnectors] = useState(false);
  const [duplicateDestinations, setDuplicateDestinations] = useState(false);
  const [destinations, setDestinations] = useState<Destinations>([]);
  
  const { data: project, loading, error } = useGet({
    path: '/projects/' + projectId,
    resolve: (project: OkResponse<Project>) => {
      shouldDuplicateCaptioning(project.result);
      shouldDuplicateConnectors(project.result);
      shouldDuplicateDestinations(project.result);
      return project.result;
    }
  });

  const shouldDuplicateCaptioning = (project: Project) => {
    const encoder = project.stream.encoders?.find(x => x.languageIsoCode === project.stream.sourceLanguageIsoCode) ?? null;

    if (encoder) {
      setDuplicateCaptioning(
        encoder.captionMode !== CaptionModeEnum.None
        && project?.projectUsers.length > 0);
    }
  };

  const shouldDuplicateConnectors = (project: Project) => {
    const encoder = project.stream.encoders?.find(x => x.languageIsoCode === project.stream.sourceLanguageIsoCode) ?? null;

    if (encoder) {
      setDuplicateConnectors(project?.projectUsers.filter(x => x.userType === UserTypeEnum.Connector).length > 0);
    }
  };

  const shouldDuplicateDestinations = (project: Project) => {
      let count = 0;

      for(let i = 0; i < (project.stream.encoders?.length ?? 0); i++) {
        const encoder = project.stream.encoders?.[i];

        count += encoder?.destinations?.length ?? 0;
      } 

      setDuplicateDestinations(count > 0);
  };

  useEffect(() => {
    if (project) {
      const result: Destinations = [];

      for(let i = 0; i < (project.stream.encoders?.length ?? 0); i++) {
        const encoder = project.stream.encoders?.[i];

        for(let j = 0; j < (encoder?.destinations?.length ?? 0); j++) {
          result.push(encoder?.destinations?.[j] as Destination);
        }
      }

      setDestinations(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    console.log(error);
    return <ErrorMessage message={t('error-messages.generic-error')} />;
  }

  if (!project) {
    return null;
  }

  return (
    <div className="project-summary-card">
      <div className="project-summary-card-header level">
        <div className="level-left project-summary-card-title" title={project.name}>
          <button onClick={onBackButton} className="project-summary-card-back-button">
            <img src={BackArrow} alt="back" className="project-summary-card-back-button-image" />
          </button>
          {project?.name && (project.name.length > 40 ? project.name.slice(0, 37) + '...' : project.name)}
        </div>
        <div className="level-right">
          <Button
            translationKey="button.duplicate-project"
            variant="secondary"
            color="blue"
            additionalClassName="btn-no-margin project-summary-card-button"
            onClick={() => setIsModalOpen(true)}
            type="button"
          />
        </div>
      </div>
      <div className="columns project-summary-card-body">
        <div className="column is-half">
          <ProjectSummaryCardBlockGeneral
            heading={t('project.general-heading')}
            project={project}
          />
          <ProjectSummaryCardBlockLinks
            heading={t('project.links.heading')}
            destinations={destinations}
            project={project}
          />
          {/* <ProjectSummaryCardBlockCaptionStyles
            heading={t('project.caption-styles.heading')}
            encoder={project.encoder as Encoder}
          /> */}
          {project?.projectUsers &&
            project.projectUsers.length > 0 &&
            project.projectUsers.map((projectUser, index) => (
              <ProjectSummaryCardBlockProjectUser
                heading={'Captioner'}
                projectUser={projectUser}
                key={index}
              />
            ))}
        </div>
        <div className="column is-half">
          <div className="project-summary-card-chat-title">
            {t('project-overview.cards.chat-history')}
          </div>
          <div className="project-summary-card-chat-panel">
            <Chat
              chatMessages={project.messages || []}
              userId={''}
              isReadOnly={true}
              isChatConnected={false}
            />
          </div>
        </div>
      </div>
      {isModalOpen && (
        <DuplicateProjectDetailsModal
          isOpen={isModalOpen}
          contentLabel="Copy Project"
          projectId={project.id}
          projectName={project.name}
          handleCloseModal={() => setIsModalOpen(false)}
          destinations={duplicateDestinations}
          captioners={duplicateCaptioning}
          connectors={duplicateConnectors}
          streamKey={true}
        />
      )}
    </div>
  );
};

export default ProjectSummaryCard;
